/**
 * Reading and writing local rows from the settings screens.
 *
 * Reads come straight out of IndexedDB and re-run whenever the outbox or a pull
 * changes something, so an edit made on another device shows up here without a
 * refresh. Writes go through `mutate()` with the row's current `version` as
 * `baseVersion`, which is what makes a concurrent edit surface as a conflict
 * instead of quietly winning.
 */

import { useCallback, useEffect, useMemo, useState } from "react";

import type { Aspect, Entity, Profile, SubQuestion } from "../../../shared/types.ts";
import { ulid } from "../../../shared/ids.ts";
import { localiseDefinition, useLocale } from "../../locale.ts";
import { getAll, idbAvailable } from "../../store/idb.ts";
import { mutate, subscribeOutbox } from "../../store/outbox.ts";
import { subscribeSync } from "../../store/sync.ts";

export interface Collection<T> {
  rows: T[];
  loading: boolean;
  error: string | null;
  reload: () => void;
}

/**
 * Every local row of one entity, tombstones filtered out.
 *
 * A pull deletes tombstoned rows outright, but a delete made on *this* device
 * is still sitting in the outbox as a row with `deleted = 1`, so the filter has
 * to happen here too or a just-deleted aspect keeps showing up.
 */
export function useLocal<T extends { id: string; deleted: 0 | 1 }>(entity: Entity): Collection<T> {
  const [rows, setRows] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    let live = true;
    if (!idbAvailable()) {
      setLoading(false);
      setError("This browser is not letting the app store anything locally.");
      return;
    }
    void (async () => {
      try {
        const all = await getAll<T>(entity);
        if (!live) return;
        setRows(all.filter((row) => row.deleted !== 1));
        setError(null);
      } catch (err) {
        if (!live) return;
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => {
      live = false;
    };
  }, [entity, nonce]);

  // Local writes and server pulls both change what is on disk under us.
  useEffect(() => {
    const offOutbox = subscribeOutbox(reload);
    const offSync = subscribeSync(() => reload());
    return () => {
      offOutbox();
      offSync();
    };
  }, [reload]);

  return { rows, loading, error, reload };
}

/**
 * The taxonomy, in the reader's language.
 *
 * The editor is fed from here, so what it shows and what it hands back are both
 * translated — `canonicalisePatch` in `client/locale.ts` is the other half, and
 * without it a save that changed nothing would pin the row to one language.
 */
export function useAspects(): Collection<Aspect> {
  const { rows, ...rest } = useLocal<Aspect>("aspect");
  const locale = useLocale();
  const shown = useMemo(() => rows.map((row) => localiseDefinition(row, locale)), [rows, locale]);
  return { rows: shown, ...rest };
}

export function useSubQuestions(): Collection<SubQuestion> {
  const { rows, ...rest } = useLocal<SubQuestion>("subquestion");
  const locale = useLocale();
  const shown = useMemo(() => rows.map((row) => localiseDefinition(row, locale)), [rows, locale]);
  return { rows: shown, ...rest };
}

export function useProfiles(): Collection<Profile> {
  return useLocal<Profile>("profile");
}

/**
 * How many frozen session snapshots point at each definition.
 *
 * This is what separates "archive" from "delete": an aspect that appears in a
 * session's snapshot is part of the record, and the editor refuses to offer a
 * permanent delete for it.
 */
export function useUsage(entity: Entity, field: string): Map<string, number> {
  const { rows } = useLocal<{ id: string; deleted: 0 | 1 } & Record<string, unknown>>(entity);
  return useMemo(() => {
    const counts = new Map<string, number>();
    for (const row of rows) {
      const key = row[field];
      if (typeof key !== "string") continue;
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return counts;
  }, [rows, field]);
}

/** Update an existing row. `version` must be the version that was read. */
export function write(
  entity: Entity,
  id: string,
  patch: Record<string, unknown>,
  version: number,
): Promise<unknown> {
  return mutate(entity, id, patch, version);
}

/** Create a row with a client-generated id. Returns the id. */
export async function createRow(entity: Entity, patch: Record<string, unknown>): Promise<string> {
  const id = ulid();
  await mutate(entity, id, patch, 0);
  return id;
}

/**
 * Permanently tombstone a row.
 *
 * Deliberately not `remove()` from the outbox: that helper puts `deleted: 1`
 * inside the patch, and the server treats `deleted` as a reserved column and
 * rejects the whole mutation — the delete would land locally and dead-letter on
 * the way up. The envelope flag is the supported channel, so an empty patch
 * plus `{ deleted: true }` is what actually replicates.
 */
export function tombstone(entity: Entity, id: string, version: number): Promise<unknown> {
  return mutate(entity, id, {}, version, { deleted: true });
}
